import { useState, useEffect } from "react";
import { X, EyeOff, Check, Loader2 } from "lucide-react";
import shopApi from "../../../../api/shopApi";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../../../components/UI/Avatar";

const HiddenConversationsModal = ({
  isOpen,
  onClose,
  shops = [],
  selectedShop,
  isGroupMode = false,
  onUnhideSuccess,
}) => {
  const [hiddenContacts, setHiddenContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isUnhiding, setIsUnhiding] = useState(false);

  // Fetch hidden contacts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchHiddenContacts();
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const fetchHiddenContacts = async () => {
    setIsLoading(true);
    try {
      if (isGroupMode) {
        // Fetch from all shops
        const allContacts = [];
        for (const shop of shops) {
          const res = await shopApi.getContacts(shop._id, 0, 1, 100, {
            categories: ["hidden"],
          });
          if (res.status === 1 && res.data) {
            allContacts.push(
              ...res.data.map((c) => ({
                ...c,
                _shopId: shop._id,
                _shopName: shop.name,
              })),
            );
          }
        }
        setHiddenContacts(allContacts);
      } else {
        const res = await shopApi.getContacts(selectedShop._id, 0, 1, 100, {
          categories: ["hidden"],
        });
        if (res.status === 1 && res.data) {
          setHiddenContacts(res.data);
        }
      }
    } catch (error) {
      console.error("Error fetching hidden contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelect = (contactId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === hiddenContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(hiddenContacts.map((c) => c._id)));
    }
  };

  const handleUnhide = async () => {
    if (selectedIds.size === 0) return;
    setIsUnhiding(true);
    try {
      const contactsToUnhide = hiddenContacts.filter((c) =>
        selectedIds.has(c._id),
      );

      for (const contact of contactsToUnhide) {
        const shopId = contact._shopId || selectedShop._id;
        await shopApi.unhideConversation(shopId, contact.uid);
      }

      // Remove unhidden contacts from list
      setHiddenContacts((prev) => prev.filter((c) => !selectedIds.has(c._id)));
      setSelectedIds(new Set());
      onUnhideSuccess?.(contactsToUnhide.length);
    } catch (error) {
      console.error("Error unhiding contacts:", error);
    } finally {
      setIsUnhiding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <EyeOff size={20} className="text-gray-500" />
            <h2 className="text-base font-semibold text-gray-800">
              Danh sách hội thoại ẩn
            </h2>
            {hiddenContacts.length > 0 && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {hiddenContacts.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Toolbar */}
        {hiddenContacts.length > 0 && (
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-50">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
              <input
                type="checkbox"
                checked={
                  selectedIds.size === hiddenContacts.length &&
                  hiddenContacts.length > 0
                }
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
              Chọn tất cả
            </label>
            {selectedIds.size > 0 && (
              <button
                onClick={handleUnhide}
                disabled={isUnhiding}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 rounded-lg transition-colors"
              >
                {isUnhiding ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <EyeOff size={14} />
                )}
                Bỏ ẩn ({selectedIds.size})
              </button>
            )}
          </div>
        )}

        {/* Contact list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : hiddenContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <EyeOff size={40} className="mb-3 text-gray-300" />
              <p className="text-sm">Không có hội thoại ẩn</p>
            </div>
          ) : (
            <div className="py-1">
              {hiddenContacts.map((contact) => (
                <label
                  key={contact._id}
                  className={`flex items-center gap-3 px-5 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedIds.has(contact._id) ? "bg-blue-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(contact._id)}
                    onChange={() => toggleSelect(contact._id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 flex-shrink-0"
                  />
                  <Avatar className="h-9 w-9 flex-shrink-0">
                    <AvatarImage src={contact.avatar} alt={contact.name} />
                    <AvatarFallback>
                      {contact.name?.charAt(0) || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {contact.name || contact.uid}
                    </p>
                    {isGroupMode && contact._shopName && (
                      <p className="text-xs text-gray-400 truncate">
                        {contact._shopName}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HiddenConversationsModal;
